// public/js/pages/settings.js
import { getCurrentUser, can, canUseApi } from '../modules/rbac.js';
import { renderTenantSettings } from '../features/pois/settings-tenant.js';
import { renderPoisSettings } from '../features/pois/settings-pois.js';
import { renderAssetFieldSettings } from '../features/assets/settings-asset-fields.js';
import { renderTrackingSettings } from '../features/settings/settings-tracking.js';
import { renderDerivedSensorSettings } from '../features/settings/settings-derived-sensors.js';
import { renderApiAccessSettings } from '../features/settings/settings-api-access.js';
import { renderDisplaySettings } from '../features/display/settings-display.js';
import { renderDocTypeSettings } from '../features/files/settings-doc-types.js';
import { getUnitSystem, setUnitSystem } from '../utils/units.js';
import { changeOwnPassword } from '../modules/users.js';
import { featureEnabled } from '../modules/features.js';

export async function initSettingsPage(container) {
  const user = getCurrentUser();
  if (!user) {
    container.innerHTML = `<div class="alert alert-danger">Not logged in</div>`;
    return;
  }
  const groups = user.groups || [];

  // Tab order is index-driven (lower = further left) so it's trivial to reorder.
  const tabs = [{ id: 'user', label: 'User', icon: 'fa-user', order: 10 }];
  // Tenant-scoped tracking behaviour (trip chaining, …) — tenant admins only.
  if (can(groups, 'manageTenants')) tabs.push({ id: 'tracking', label: 'Tracking', icon: 'fa-route', order: 15 });
  if (can(groups, 'manageTenants')) tabs.push({ id: 'assetfields', label: 'Asset Fields', icon: 'fa-list-check', order: 20 });
  if (can(groups, 'manageTenants')) tabs.push({ id: 'derived', label: 'Derived sensors', icon: 'fa-wave-square', order: 25 });
  // API access — only when the tenant holds the paid `api` add-on.
  if (can(groups, 'manageTenants') && canUseApi()) tabs.push({ id: 'apiaccess', label: 'API access', icon: 'fa-key', order: 28 });
  // Display templates (list columns, map card, pin labels) — every user may set
  // their own; the tenant default needs manageTenants (the tab handles both).
  tabs.push({ id: 'display', label: 'Display', icon: 'fa-table-columns', order: 22 });
  // Document types for the file store — tenant admins only, and only where the
  // file store itself is switched on: its endpoints live behind the same flag,
  // so the tab would otherwise render and then fail on its first request.
  if (featureEnabled('assetFiles') && can(groups, 'manageDocTypes')) tabs.push({ id: 'doctypes', label: 'Document types', icon: 'fa-folder-tree', order: 26 });
  if (can(groups, 'listPois')) tabs.push({ id: 'pois', label: 'POIs', icon: 'fa-location-dot', order: 30 });
  if (can(groups, 'managePoiTypes')) tabs.push({ id: 'tenant', label: 'POI Settings', icon: 'fa-map-location-dot', order: 40 });
  tabs.sort((a, b) => a.order - b.order);

  // Each tab has its own address: #settings/<tab>. An unknown or not-permitted
  // sub-path falls back to the first tab this user actually has.
  const wanted = (window.location.hash || '').split('/')[1]?.toLowerCase();
  const active = tabs.find(t => t.id === wanted) || tabs[0];
  // Normalise a bare #settings (or a bad sub-path) WITHOUT adding a history
  // entry, so Back from the first tab leaves Settings instead of bouncing.
  setHash(active.id, true);

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="h4 mb-0">Settings <span class="badge bg-primary ms-2">User: ${user.email || user.username}</span></h2>
    </div>

    <ul class="nav nav-tabs mb-3" id="settings-tabs" role="tablist">
      ${tabs.map(t => `
        <li class="nav-item" role="presentation">
          <button class="nav-link ${t.id === active.id ? 'active' : ''}" id="tab-${t.id}" data-bs-toggle="tab"
                  data-bs-target="#pane-${t.id}" type="button" role="tab">
            <i class="fa-solid ${t.icon} me-2"></i>${t.label}
          </button>
        </li>`).join('')}
    </ul>

    <div class="tab-content">
      ${tabs.map(t => `<div class="tab-pane fade ${t.id === active.id ? 'show active' : ''}" id="pane-${t.id}" role="tabpanel"></div>`).join('')}
    </div>
  `;

  // The tab named by the address renders immediately; the others lazily on
  // first show. Marking a tab active in the markup fires no Bootstrap event,
  // which is why this one is rendered by hand.
  const rendered = {};
  const renderTab = (id) => {
    if (rendered[id]) return;
    rendered[id] = true;
    const pane = document.getElementById(`pane-${id}`);
    if (!pane) return;
    if (id === 'user') renderUserSettings(pane);
    else if (id === 'tenant') renderTenantSettings(pane);
    else if (id === 'tracking') renderTrackingSettings(pane);
    else if (id === 'assetfields') renderAssetFieldSettings(pane);
    else if (id === 'derived') renderDerivedSensorSettings(pane);
    else if (id === 'apiaccess') renderApiAccessSettings(pane);
    else if (id === 'display') renderDisplaySettings(pane);
    else if (id === 'doctypes') renderDocTypeSettings(pane);
    else if (id === 'pois') renderPoisSettings(pane);
  };
  renderTab(active.id);

  document.querySelectorAll('#settings-tabs button[data-bs-toggle="tab"]').forEach(btn => {
    btn.addEventListener('shown.bs.tab', () => {
      const id = btn.id.replace('tab-', '');
      renderTab(id);
      // pushState gives Back/Forward a real stop per tab. It deliberately fires
      // no hashchange, so switching tabs does NOT send the router through a full
      // page rebuild — only an actual Back/Forward does, and that lands on the
      // right tab because the address names it.
      setHash(id, false);
    });
  });
}

// Write #settings/<tab> into the address bar. `replace` swaps the current entry
// instead of adding one.
function setHash(tabId, replace) {
  const url = `#settings/${tabId}`;
  if (window.location.hash === url) return;
  try {
    history[replace ? 'replaceState' : 'pushState'](null, '', url);
  } catch (e) { /* history is unavailable (file://) — the tabs still work */ }
}

// ── User settings ─────────────────────────────────────────────────────────────
// Previous options (default map zoom, clustering toggle, moving-tracker
// highlight) were removed: the map always fits to all pins, and clustering is
// toggled directly on the map page. The tab is kept as a placeholder for future
// per-user preferences.
function renderUserSettings(container) {
  const sys = getUnitSystem();
  container.innerHTML = `
    <div class="card shadow-sm">
      <div class="card-body">
        <h6 class="mb-1">Units</h6>
        <p class="text-muted small mb-3">Choose how temperatures, speeds and distances are shown across the app.</p>
        <div class="unit-toggle" id="settings-unit-toggle" role="group" aria-label="Unit system">
          <button type="button" class="unit-opt ${sys === 'metric' ? 'active' : ''}" data-system="metric">Metric</button>
          <button type="button" class="unit-opt ${sys === 'imperial' ? 'active' : ''}" data-system="imperial">Imperial</button>
        </div>
      </div>
    </div>

    <div class="card shadow-sm mt-3">
      <div class="card-body">
        <h6 class="mb-1">Change password</h6>
        <p class="text-muted small mb-3">Set a new password for your account. You stay logged in on this device.</p>
        <form id="pw-form" autocomplete="off" style="max-width:380px;">
          <div class="mb-2">
            <label class="form-label small mb-1" for="pw-current">Current password</label>
            <input id="pw-current" type="password" class="form-control form-control-sm" autocomplete="current-password" required>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-1" for="pw-new">New password</label>
            <input id="pw-new" type="password" class="form-control form-control-sm" autocomplete="new-password" minlength="8" required>
            <div class="form-text">At least 8 characters.</div>
          </div>
          <div class="mb-3">
            <label class="form-label small mb-1" for="pw-confirm">Repeat new password</label>
            <input id="pw-confirm" type="password" class="form-control form-control-sm" autocomplete="new-password" required>
          </div>
          <button type="submit" id="pw-submit" class="btn btn-primary btn-sm">Change password</button>
        </form>
      </div>
    </div>
  `;

  const pwForm = container.querySelector('#pw-form');
  pwForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const current = container.querySelector('#pw-current').value;
    const next = container.querySelector('#pw-new').value;
    const confirmVal = container.querySelector('#pw-confirm').value;
    if (next !== confirmVal) {
      window.showToast('The new passwords do not match', 'warning');
      return;
    }
    if (next === current) {
      window.showToast('The new password must differ from the current one', 'warning');
      return;
    }
    const btn = container.querySelector('#pw-submit');
    btn.disabled = true;
    try {
      await changeOwnPassword(current, next);
      pwForm.reset();
      window.showToast('Password changed', 'success');
    } catch (err) {
      window.showToast(err?.message || 'Failed to change password', 'danger');
    } finally {
      btn.disabled = false;
    }
  });

  const toggle = container.querySelector('#settings-unit-toggle');
  const paint = () => {
    const cur = getUnitSystem();
    toggle.querySelectorAll('.unit-opt').forEach(b => b.classList.toggle('active', b.dataset.system === cur));
  };
  toggle.querySelectorAll('.unit-opt').forEach(btn => {
    btn.addEventListener('click', () => setUnitSystem(btn.dataset.system));
  });
  // Stay in sync if the topbar toggle changes it.
  window.addEventListener('unitsystemchange', paint);
}
